import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';
import api from "../pages/api";
import { REFRESH_TOKEN, ACCESS_TOKEN } from "../pages/constants";
import Cookies from 'js-cookie';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const forceDevAuth = import.meta.env.DEV && import.meta.env.VITE_FORCE_AUTH === 'true';
    const [isAuthorized, setIsAuthorized] = useState(forceDevAuth);
    const [isLoading, setIsLoading] = useState(true);
    const [sessionId, setSessionId] = useState(Cookies.get('session_id'));

    const updateSession = useCallback((newSessionId) => {
        if (newSessionId) {
            setSessionId(newSessionId);
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        const checkAuth = async () => {
            if (forceDevAuth) {
                if (isMounted) {
                    setIsAuthorized(true);
                    setIsLoading(false);
                }
                return;
            }

            const token = localStorage.getItem(ACCESS_TOKEN);
            if (!token) {
                if (isMounted) {
                    setIsAuthorized(false);
                    setIsLoading(false);
                }
                return;
            }

            try {
                const decoded = jwtDecode(token);
                const tokenExpiration = decoded.exp;
                const now = Date.now() / 1000;

                if (tokenExpiration < now) {
                    await refreshToken(abortController.signal);
                } else if (isMounted) {
                    setIsAuthorized(true);
                }
            } catch (error) {
                if (!abortController.signal.aborted) {
                    console.error("Error decoding token:", error);
                    setIsAuthorized(false);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        checkAuth();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [forceDevAuth]);

    const refreshToken = useCallback(async (signal) => {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN);
        if (!refreshToken) {
            setIsAuthorized(false);
            return;
        }

        try {
            const res = await api.post("/api/token/refresh/", 
                { refresh: refreshToken },
                { signal: signal }
            );

            if (res.status === 200) {
                localStorage.setItem(ACCESS_TOKEN, res.data.access);
                setIsAuthorized(true);
            } else {
                setIsAuthorized(false);
            }
        } catch (error) {
            if (!signal.aborted) {
                console.error("Error refreshing token:", error);
                setIsAuthorized(false);
                localStorage.removeItem(ACCESS_TOKEN);
                localStorage.removeItem(REFRESH_TOKEN);
            }
        }
    }, []);

    const login = useCallback(async (accessToken, refreshToken) => {
        localStorage.setItem(ACCESS_TOKEN, accessToken);
        localStorage.setItem(REFRESH_TOKEN, refreshToken);
        setIsAuthorized(true);
    }, []);

    const logout = useCallback(async () => {
        try {
            const refreshToken = localStorage.getItem(REFRESH_TOKEN);
            if (refreshToken) {
                await api.post("/api/logout/", {
                    refresh: refreshToken
                });
            }
        } catch (error) {
            console.error("Error during logout:", error);
        } finally {
            localStorage.removeItem(ACCESS_TOKEN);
            localStorage.removeItem(REFRESH_TOKEN);
            setIsAuthorized(false);
        }
    }, []);

    const contextValue = useMemo(() => ({
        isAuthorized,
        isLoading,
        login,
        logout,
        sessionId,
        updateSession
    }), [isAuthorized, isLoading, login, logout, sessionId, updateSession]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
